import { useState, type FormEvent } from "react";

import { ApiFehler } from "../api/client";
import { useKundeAnlegen, useKunden } from "../api/hooks";
import type { Kunde } from "../api/typen";
import { useToast } from "../kontext/ToastKontext";
import { pruefeEmail } from "../lib/validierung";
import { Knopf, Modal, Spinner } from "./Basis";

const VOLLMACHT_TEXT: Record<Kunde["vollmacht_status"], string> = {
  fehlt: "Vollmacht fehlt",
  hochgeladen: "Vollmacht hochgeladen",
  geprueft: "Vollmacht geprüft ✓",
  abgelehnt: "Vollmacht abgelehnt",
};

function NeuerKundeDialog({ offen, beiSchliessen, beiAngelegt }: { offen: boolean; beiSchliessen: () => void; beiAngelegt: (k: Kunde) => void }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [fehler, setFehler] = useState<{ name?: string; email?: string }>({});
  const anlegen = useKundeAnlegen();
  const toast = useToast();

  function absenden(e: FormEvent) {
    e.preventDefault();
    const neu = {
      name: name.trim().length < 2 ? "Name muss mindestens 2 Zeichen haben" : undefined,
      email: pruefeEmail(email) ?? undefined,
    };
    setFehler(neu);
    if (neu.name || neu.email) return;
    anlegen.mutate(
      { name: name.trim(), email: email.trim() },
      {
        onSuccess: (kunde) => {
          toast.erfolg("Kunde angelegt", kunde.name);
          setName("");
          setEmail("");
          beiAngelegt(kunde);
        },
        onError: (err) => {
          if (err instanceof ApiFehler && err.status === 409) setFehler({ email: err.message });
          else if (err instanceof ApiFehler && err.status === 422) setFehler(err.feldFehler);
          else toast.fehler(err, "Kunde konnte nicht angelegt werden");
        },
      },
    );
  }

  return (
    <Modal
      offen={offen}
      titel="Neuen Kunden anlegen"
      beiSchliessen={beiSchliessen}
      fuss={
        <>
          <Knopf onClick={beiSchliessen}>Abbrechen</Knopf>
          <Knopf variante="primaer" type="submit" form="neuer-kunde" laedt={anlegen.isPending}>
            Anlegen
          </Knopf>
        </>
      }
    >
      <form id="neuer-kunde" onSubmit={absenden} className="space-y-4" noValidate>
        <div>
          <label htmlFor="kunde-name" className="mb-1 block text-xs font-semibold">Name (Anlagenbetreiber)</label>
          <input id="kunde-name" className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm" value={name} onChange={(e) => setName(e.target.value)} data-autofokus />
          {fehler.name && <p className="mt-1 text-xs text-ampel-rot">{fehler.name}</p>}
        </div>
        <div>
          <label htmlFor="kunde-email" className="mb-1 block text-xs font-semibold">E-Mail</label>
          <input id="kunde-email" type="email" className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm" value={email} onChange={(e) => setEmail(e.target.value)} />
          {fehler.email && <p className="mt-1 text-xs text-ampel-rot">{fehler.email}</p>}
        </div>
      </form>
    </Modal>
  );
}

export function KundeAuswahl({ wert, beiAuswahl }: { wert: number | null; beiAuswahl: (id: number | null) => void }) {
  const kunden = useKunden();
  const [dialog, setDialog] = useState(false);
  const aktuell = kunden.data?.items.find((k) => k.id === wert);

  return (
    <div>
      <label htmlFor="kunde" className="mb-1 block text-xs font-semibold text-neutral-700">
        Kunde (Anlagenbetreiber)
      </label>
      <div className="flex gap-2">
        <select
          id="kunde"
          className="min-w-0 flex-1 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm"
          value={wert ?? ""}
          onChange={(e) => beiAuswahl(e.target.value ? Number(e.target.value) : null)}
          disabled={kunden.isLoading}
        >
          <option value="">{kunden.isLoading ? "Lädt…" : "– Kunde auswählen –"}</option>
          {kunden.data?.items.map((k) => (
            <option key={k.id} value={k.id}>
              {k.name} · {k.email}
            </option>
          ))}
        </select>
        <Knopf onClick={() => setDialog(true)}>+ Neuer Kunde</Knopf>
      </div>
      {kunden.isLoading && <Spinner klein />}
      {aktuell && (
        <p className="mt-1 text-xs text-neutral-500">
          {aktuell.anlagen_anzahl} Anlage(n) · {aktuell.dokumente_anzahl} Dokument(e) · {VOLLMACHT_TEXT[aktuell.vollmacht_status]}
        </p>
      )}
      <NeuerKundeDialog
        offen={dialog}
        beiSchliessen={() => setDialog(false)}
        beiAngelegt={(kunde) => {
          setDialog(false);
          beiAuswahl(kunde.id);
        }}
      />
    </div>
  );
}
